import { useState } from "react";
import { api } from "../lib/api";
import { uploadFile, fileViewUrl } from "../lib/files";
import { InitialsAvatar } from "./InitialsAvatar";
import type { Participant } from "../lib/types";

/**
 * The participant photo, editable — one component serving the admin Settings
 * tab and the partner portal alike. The file lands in stored_file and the
 * record points at it; who may SEE it is decided by the stored_file policy
 * (065), so this component never needs to know which portal it is in.
 */
export const ParticipantPhotoCard = ({ participant, onSaved }: {
    participant: Participant;
    onSaved: () => void;
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const upload = async (file: File) => {
        setBusy(true);
        setError(null);
        try {
            const stored = await uploadFile(file, "general");
            await api.put(`/participants/${participant.id}`, { photo_file_id: stored.id });
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Photo upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="card">
            <div className="card-head"><h2>Photo</h2></div>
            <div className="card-body">
                {error && <div className="error-banner">{error}</div>}
                <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                    <InitialsAvatar name={participant.full_name} size={64}
                        photoUrl={participant.photo_file_id ? fileViewUrl(participant.photo_file_id) : null} />
                    <div>
                        <label className="btn ghost small" style={{ cursor: "pointer" }}>
                            {participant.photo_file_id ? "Change photo" : "Add photo"}
                            <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                                disabled={busy}
                                onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
                        </label>
                        <p style={{ fontSize: "0.74rem", color: "var(--muted)", marginTop: "0.35rem" }}>
                            {busy ? "Uploading…" : "JPEG or PNG. A clear head-and-shoulders photo works best."}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
